import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, message, Space, Card, Row, Col } from 'antd';
import { PlusOutlined, ThunderboltOutlined, CalendarOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const RecurringExpenses = () => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [generateModalVisible, setGenerateModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [generateForm] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: recurringExpenses, isLoading } = useQuery('recurringExpenses', async () => {
        const response = await api.get('/recurring-expenses');
        return response.data.data;
    });

    const { data: expenseTypes } = useQuery('expenseTypes', async () => {
        const response = await api.get('/expense-types');
        return response.data.data;
    });

    const { data: staffData } = useQuery('staff', async () => {
        const response = await api.get('/staff');
        return response.data.data;
    });

    const createMutation = useMutation(
        (data) => api.post('/recurring-expenses', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('recurringExpenses');
                message.success(t('recurringExpenseAdded'));
                setIsModalVisible(false);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToAddRecurringExpense'));
            },
        }
    );

    const updateMutation = useMutation(
        ({ id, data }) => api.put(`/recurring-expenses/${id}`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('recurringExpenses');
                message.success(t('recurringExpenseUpdated'));
                setIsModalVisible(false);
                setEditingExpense(null);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUpdateRecurringExpense'));
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => api.delete(`/recurring-expenses/${id}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('recurringExpenses');
                message.success(t('recurringExpenseDeleted'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToDeleteRecurringExpense'));
            },
        }
    );

    const generateMutation = useMutation(
        (data) => api.post('/recurring-expenses/generate-monthly', data),
        {
            onSuccess: (response) => {
                queryClient.invalidateQueries('expenses');
                queryClient.invalidateQueries('recurringExpenses');
                message.success(`${t('monthlyExpensesGenerated')}: ${response.data.data.generated} ${t('expenses')}`);
                setGenerateModalVisible(false);
                generateForm.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToGenerateExpenses'));
            },
        }
    );

    const columns = [
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: t('expenseType'),
            dataIndex: 'expenseType',
            key: 'expenseType'
        },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => {
                const curr = record.currency || 'USD';
                return curr === 'IQD' ? `IQD ${amount.toLocaleString()}` : `$${amount.toFixed(2)}`;
            }
        },
        {
            title: t('frequency'),
            dataIndex: 'frequency',
            key: 'frequency',
            render: (freq) => t(freq)
        },
        {
            title: t('dayOfMonth'),
            dataIndex: 'dayOfMonth',
            key: 'dayOfMonth'
        },
        {
            title: t('status'),
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <span style={{
                    color: isActive ? '#52c41a' : '#ff4d4f',
                    fontWeight: 500
                }}>
                    {isActive ? t('active') : t('inactive')}
                </span>
            )
        },
        {
            title: t('lastGenerated'),
            dataIndex: 'lastGenerated',
            key: 'lastGenerated',
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {!record.isStaff && (
                        <>
                            <Button
                                type="default"
                                size="small"
                                onClick={() => handleEdit(record)}
                                style={{
                                    borderColor: '#667eea',
                                    color: '#667eea',
                                    borderRadius: '6px'
                                }}
                            >
                                {t('edit')}
                            </Button>
                            <Button
                                type="default"
                                size="small"
                                danger
                                onClick={() => handleDelete(record._id)}
                                style={{ borderRadius: '6px' }}
                            >
                                {t('delete')}
                            </Button>
                        </>
                    )}
                    {record.isStaff && (
                        <span style={{ color: '#999', fontSize: '12px' }}>
                            {t('autoGenerated')}
                        </span>
                    )}
                </Space>
            ),
        },
    ];

    const handleEdit = (record) => {
        setEditingExpense(record);
        form.setFieldsValue({
            ...record,
            startDate: record.startDate ? dayjs(record.startDate) : null,
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('confirmDeleteRecurringExpense'),
            okText: t('delete'),
            okType: 'danger',
            cancelText: t('cancel'),
            onOk: () => deleteMutation.mutate(id),
        });
    };

    const handleSubmit = (values) => {
        const payload = {
            ...values,
            startDate: values.startDate ? values.startDate.toISOString() : new Date().toISOString(),
        };

        if (editingExpense) {
            updateMutation.mutate({ id: editingExpense._id, data: payload });
        } else {
            createMutation.mutate(payload);
        }
    };

    const handleGenerate = (values) => {
        generateMutation.mutate(values);
    };

    // Combine recurring expenses and staff salaries for display
    const combinedData = React.useMemo(() => {
        const recurring = recurringExpenses || [];
        const activeStaff = (staffData || []).filter(staff => staff.status === 'active');

        const staffAsRecurring = activeStaff.map(staff => ({
            _id: `staff-${staff._id}`,
            name: `${staff.firstName} ${staff.lastName} - ${t('salary')}`,
            expenseType: 'Salary',
            amount: staff.salary?.amount || 0,
            currency: staff.salary?.currency || 'USD',
            frequency: 'monthly',
            dayOfMonth: 28,
            isActive: true,
            isStaff: true,
            staffId: staff._id
        }));

        return [...recurring, ...staffAsRecurring];
    }, [recurringExpenses, staffData, t]);

    return (
        <div>
            <div className="page-header">
                <h1>{t('recurringExpenses')}</h1>
                <Space>
                    {/* <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        onClick={() => setGenerateModalVisible(true)}
                        style={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            border: 'none',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px rgba(240, 147, 251, 0.4)'
                        }}
                    >
                        {t('generateMonthlyExpenses')}
                    </Button> */}
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingExpense(null);
                            form.resetFields();
                            setIsModalVisible(true);
                        }}
                    >
                        {t('addRecurringExpense')}
                    </Button>
                </Space>
            </div>

            <Card style={{ marginBottom: 24, borderRadius: '12px' }}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#f0f5ff', borderRadius: '8px' }}>
                            <h3 style={{ margin: 0, marginBottom: 8, color: '#1890ff' }}>
                                <CalendarOutlined /> {t('howItWorks')}
                            </h3>
                            <p style={{ margin: 0, color: '#666' }}>
                                {t('recurringExpensesDescription')}
                            </p>
                        </div>
                    </Col>
                </Row>
            </Card>

            <Table
                columns={columns}
                dataSource={combinedData}
                rowKey="_id"
                loading={isLoading}
            />

            {/* Add/Edit Modal */}
            <Modal
                title={editingExpense ? t('editRecurringExpense') : t('addRecurringExpense')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingExpense(null);
                    form.resetFields();
                }}
                footer={null}
                width="min(600px, 95vw)"
                centered
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="name"
                        label={t('expenseName')}
                        rules={[{ required: true, message: t('pleaseEnterExpenseName') }]}
                    >
                        <Input placeholder={t('expenseNamePlaceholder')} />
                    </Form.Item>

                    <Form.Item
                        name="expenseType"
                        label={t('expenseType')}
                        rules={[{ required: true, message: t('pleaseSelectExpenseType') }]}
                    >
                        <Select
                            placeholder={t('selectExpenseType')}
                        >
                            {expenseTypes?.map(type => (
                                <Select.Option key={type._id} value={type.name}>
                                    {type.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="description" label={t('description')}>
                        <Input.TextArea rows={3} placeholder={t('descriptionPlaceholder')} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label={t('amount')}
                                rules={[{ required: true, message: t('pleaseEnterAmount') }]}
                            >
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="currency"
                                label={t('currency')}
                                rules={[{ required: true, message: t('pleaseSelectCurrency') }]}
                                initialValue="USD"
                            >
                                <Select>
                                    <Select.Option value="USD">USD ($)</Select.Option>
                                    <Select.Option value="IQD">IQD (د.ع)</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="frequency"
                                label={t('frequency')}
                                rules={[{ required: true }]}
                                initialValue="monthly"
                            >
                                <Select>
                                    <Select.Option value="monthly">{t('monthly')}</Select.Option>
                                    <Select.Option value="quarterly">{t('quarterly')}</Select.Option>
                                    <Select.Option value="yearly">{t('yearly')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="dayOfMonth"
                                label={t('dayOfMonth')}
                                rules={[{ required: true }]}
                                initialValue={1}
                            >
                                <InputNumber style={{ width: '100%' }} min={1} max={28} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="startDate" label={t('startDate')}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label={t('status')}
                        initialValue={true}
                    >
                        <Select>
                            <Select.Option value={true}>{t('active')}</Select.Option>
                            <Select.Option value={false}>{t('inactive')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={createMutation.isLoading || updateMutation.isLoading}
                            >
                                {editingExpense ? t('update') : t('create')}
                            </Button>
                            <Button onClick={() => setIsModalVisible(false)}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Generate Monthly Expenses Modal */}
            <Modal
                title={t('generateMonthlyExpenses')}
                open={generateModalVisible}
                onCancel={() => {
                    setGenerateModalVisible(false);
                    generateForm.resetFields();
                }}
                footer={null}
                width="min(400px, 95vw)"
                centered
                destroyOnClose
            >
                <Form
                    form={generateForm}
                    layout="vertical"
                    onFinish={handleGenerate}
                    initialValues={{
                        month: new Date().getMonth() + 1,
                        year: new Date().getFullYear()
                    }}
                >
                    <p style={{ marginBottom: 16, color: '#666' }}>
                        {t('generateExpensesDescription')}
                    </p>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="month"
                                label={t('month')}
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Select.Option value={1}>{t('january')}</Select.Option>
                                    <Select.Option value={2}>{t('february')}</Select.Option>
                                    <Select.Option value={3}>{t('march')}</Select.Option>
                                    <Select.Option value={4}>{t('april')}</Select.Option>
                                    <Select.Option value={5}>{t('may')}</Select.Option>
                                    <Select.Option value={6}>{t('june')}</Select.Option>
                                    <Select.Option value={7}>{t('july')}</Select.Option>
                                    <Select.Option value={8}>{t('august')}</Select.Option>
                                    <Select.Option value={9}>{t('september')}</Select.Option>
                                    <Select.Option value={10}>{t('october')}</Select.Option>
                                    <Select.Option value={11}>{t('november')}</Select.Option>
                                    <Select.Option value={12}>{t('december')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="year"
                                label={t('year')}
                                rules={[{ required: true }]}
                            >
                                <InputNumber style={{ width: '100%' }} min={2020} max={2100} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setGenerateModalVisible(false)}>
                                {t('cancel')}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={generateMutation.isLoading}
                                icon={<ThunderboltOutlined />}
                            >
                                {t('generate')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default RecurringExpenses;
